import type { ResourceAvailabilityPeriod } from '@/types/scheduling-api';
import { parseWallClock } from '@/shared/lib/wall-clock';
import { formatClockTime } from '@/shared/lib/time-format';

/**
 * Device presence / availability (PROD-2007). Pure helpers over the
 * `GET /api/Slots/ResourceAvailability` response: shading geometry for the
 * device column and the bookable windows the Book-a-device flow offers.
 *
 * Time-zone rule: the controller moves every period into the CALLER's zone
 * before serialising (`response.Move(y - x)`), so the wall clock in the string
 * is already local. The `Kind` of the moved `DateTime` can still print a `Z`
 * suffix, which would make `new Date(iso)` re-shift it by the user's offset.
 * Periods are therefore parsed TEXTUALLY — date + time digits only — and any
 * suffix (`Z`, `+04:00`) is ignored on purpose.
 */

export interface AvailabilityWindow {
  start: Date;
  end: Date;
}

/**
 * Textual local parse — see the module header. Lives in `@/shared/lib/wall-clock`
 * since PROD-2527 so the blocker dialogs (scheduling AND settings) read the
 * same contract through the same parser; re-exported here for existing callers.
 */
export { parseWallClock };

/** Parse + sanitise the response: valid, non-empty windows sorted by start. */
export function parseAvailability(
  periods: readonly ResourceAvailabilityPeriod[] | null | undefined,
): AvailabilityWindow[] {
  const out: AvailabilityWindow[] = [];
  for (const p of periods ?? []) {
    const start = parseWallClock(p?.start);
    const end = parseWallClock(p?.end);
    if (!start || !end) continue;
    if (end.getTime() <= start.getTime()) continue;
    out.push({ start, end });
  }
  out.sort((a, b) => a.start.getTime() - b.start.getTime());
  return out;
}

/**
 * Ruling: a device with no working hours returns `[]` → no shading and a
 * "Not bookable" marker on the column header. `undefined` (still loading /
 * errored) is NOT "not bookable" — the header stays neutral until we know.
 */
export function isNotBookable(
  periods: readonly ResourceAvailabilityPeriod[] | null | undefined,
): boolean {
  return Array.isArray(periods) && periods.length === 0;
}

function minuteOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Windows clipped to one calendar day (a window crossing midnight is cut at
 * the day's edges). Windows that don't touch the day are dropped.
 */
export function windowsForDay(
  windows: readonly AvailabilityWindow[],
  day: Date,
): AvailabilityWindow[] {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1, 0, 0, 0, 0);
  const out: AvailabilityWindow[] = [];
  for (const w of windows) {
    const start = w.start.getTime() < dayStart.getTime() ? dayStart : w.start;
    const end = w.end.getTime() > dayEnd.getTime() ? dayEnd : w.end;
    if (end.getTime() <= start.getTime()) continue;
    out.push({ start, end });
  }
  return out;
}

/**
 * Pixel geometry of a window inside an hour grid (`startHour` inclusive,
 * `endHour` exclusive — the bottom of the last row). `null` when the window
 * lies wholly outside the visible hours. A window ending at midnight of the
 * next day is treated as ending at 24:00 of the grid's day.
 */
export function windowToGridGeometry(
  w: AvailabilityWindow,
  startHour: number,
  endHour: number,
  hourHeight: number,
): { top: number; height: number } | null {
  const gridStart = startHour * 60;
  const gridEnd = endHour * 60;
  const startMin = minuteOfDay(w.start);
  const endMinRaw = minuteOfDay(w.end);
  // 00:00 next day → 24:00 for the day the window started on.
  const endMin = endMinRaw === 0 && !sameDay(w.start, w.end) ? 24 * 60 : endMinRaw;
  const top = Math.max(startMin, gridStart);
  const bottom = Math.min(endMin, gridEnd);
  if (bottom <= top) return null;
  return {
    top: ((top - gridStart) / 60) * hourHeight,
    height: ((bottom - top) / 60) * hourHeight,
  };
}

/**
 * Every start at which a booking of `minutes` fits inside a window, stepping
 * from each window's start by `stepMinutes` (defaults to the product's own
 * minutes — "snap to the product's minutes"). Windows shorter than the
 * product yield nothing.
 *
 * `notBefore` drops starts that have already passed (PROD-2444). It lives HERE
 * rather than in each rail because the enumeration is the only place that knows
 * every start it produced: a caller filtering afterwards has to remember to,
 * and the Book rail shipped for a week offering `9:00a` at five in the
 * afternoon precisely because nothing between the window and the chip strip
 * ever looked at the clock.
 *
 * Omitted (the default) it enumerates the whole window, which is what the
 * availability SHADING on the device column wants — a morning that has passed
 * is still the device's working hours and must still be drawn.
 *
 * Boundary: a start EQUAL to `notBefore` is dropped. A window opening exactly
 * now is not bookable by the time the operator has picked a patient, and the
 * server would refuse it anyway.
 */
export function bookableStarts(
  windows: readonly AvailabilityWindow[],
  minutes: number,
  stepMinutes: number = minutes,
  notBefore?: Date | null,
): Date[] {
  if (!(minutes > 0)) return [];
  const step = stepMinutes > 0 ? stepMinutes : minutes;
  const floor = notBefore ? notBefore.getTime() : null;
  const out: Date[] = [];
  for (const w of windows) {
    let cursor = new Date(w.start);
    while (cursor.getTime() + minutes * 60_000 <= w.end.getTime()) {
      if (floor === null || cursor.getTime() > floor) out.push(new Date(cursor));
      cursor = new Date(cursor.getTime() + step * 60_000);
    }
  }
  return out;
}

/** True when `[start, start + minutes)` has already begun. */
export function startHasPassed(start: Date, now: Date): boolean {
  return start.getTime() <= now.getTime();
}

/** True when `[start, start + minutes)` sits inside one window. */
export function fitsWindow(
  windows: readonly AvailabilityWindow[],
  start: Date,
  minutes: number,
): boolean {
  const s = start.getTime();
  const e = s + minutes * 60_000;
  return windows.some((w) => w.start.getTime() <= s && e <= w.end.getTime());
}

/**
 * The first bookable start strictly after `after` — what the flow offers when
 * the server answers `ResourceBooked` (the window went meanwhile).
 */
export function nextBookableStart(
  windows: readonly AvailabilityWindow[],
  minutes: number,
  after: Date,
): Date | null {
  const t = after.getTime();
  for (const s of bookableStarts(windows, minutes)) {
    if (s.getTime() > t) return s;
  }
  return null;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local, no-`Z` `YYYY-MM-DDTHH:mm:ss` (the calendar's row/tile ISO form). */
export function localNoZ(d: Date): string {
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** "09:00" / "14:30" (24h, the standard) or "9:00a" / "2:30p" — the calendar's clock (shared/lib/time-format). */
export function formatClock(d: Date): string {
  return formatClockTime(d);
}

/**
 * Extend an hour gutter so every availability window is visible — a device's
 * own hours can start before / end after the practice's working hours that
 * `hoursForDayList` derives the gutter from. Returns a copy of the same range
 * when nothing needs extending.
 */
export function hoursCoveringWindows(
  hours: readonly number[],
  windows: readonly AvailabilityWindow[],
): number[] {
  if (windows.length === 0 || hours.length === 0) return [...hours];
  let first = hours[0];
  let last = hours[hours.length - 1];
  for (const w of windows) {
    first = Math.min(first, w.start.getHours());
    const endMin = minuteOfDay(w.end);
    const lastHour =
      endMin === 0 && !sameDay(w.start, w.end) ? 23 : Math.max(0, Math.ceil(endMin / 60) - 1);
    last = Math.max(last, lastHour);
  }
  first = Math.max(0, first);
  last = Math.min(23, last);
  if (first === hours[0] && last === hours[hours.length - 1]) return [...hours];
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}
